import { Clock, Copy, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  formatWorkoutDuration,
  sumExerciseCalories,
  sumExerciseDurationParts,
} from '../../core/utils/workoutDuration';

function ExerciseItemCard({
  workout,
  onEdit,
  onDelete,
  onCopyToOtherDays,
  isSequenceMode = false,
  onMoveLeft,
  onMoveRight,
  canMoveLeft = false,
  canMoveRight = false,
}) {
  const totalCalories = workout.base_calories_per_rep
    ? sumExerciseCalories(workout).toFixed(0)
    : null;

  const durationParts = sumExerciseDurationParts(workout);
  const totalSeconds =
    durationParts.workSeconds +
    durationParts.setRestSeconds +
    durationParts.restAfterSeconds;
  const totalDurationLabel = formatWorkoutDuration(totalSeconds);

  return (
    <div
      className={`w-[280px] p-4 bg-white border rounded-xl flex flex-col transition-all ${
        isSequenceMode ? 'border-gray-300' : 'border-gray-200 shadow-sm hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {workout.workout_type || 'Exercise'}
          </h3>
          {workout.sets?.length > 0 ? (
            <div className="mt-2 space-y-0.5">
              {workout.sets.map((set, index) => (
                <p key={index} className="text-xs text-gray-600">
                  {set.reps ?? set.target_reps ?? 0} reps · {set.time_seconds ?? set.target_time_seconds ?? 0}s ·{' '}
                  {set.rest_time_seconds ?? set.target_rest_seconds ?? 0}s rest · {set.target_weight ?? set.weight ?? 0} kg
                </p>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 mt-1">No sets</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {totalCalories && (
              <p className="text-xs font-medium text-gray-700">{totalCalories} cal</p>
            )}
            {totalDurationLabel && (
              <p className="text-xs font-medium text-gray-700 inline-flex items-center gap-1">
                <Clock className="w-3 h-3 text-orange-500" aria-hidden />
                {totalDurationLabel}
              </p>
            )}
          </div>
        </div>
        <div className="shrink-0 text-center bg-gray-50 rounded-lg px-2 py-1 border border-gray-200">
          <p className="text-sm font-semibold text-gray-800">{workout.rest_time_after || 0}</p>
          <p className="text-[10px] text-gray-500">sec rest</p>
        </div>
      </div>

      {totalDurationLabel && (
        <div className="mt-2 grid grid-cols-3 gap-1 rounded-lg bg-gray-50 border border-gray-100 px-2 py-1.5">
          {[
            { key: 'work', label: 'Work', seconds: durationParts.workSeconds },
            { key: 'setRest', label: 'Set rest', seconds: durationParts.setRestSeconds },
            { key: 'restAfter', label: 'Rest after', seconds: durationParts.restAfterSeconds },
          ].map((cell) => (
            <div key={cell.key} className="flex flex-col items-center gap-0.5">
              <span className="text-[10px] text-gray-500">{cell.label}</span>
              <span className="text-[11px] font-semibold text-gray-800">
                {formatWorkoutDuration(cell.seconds, { emptyAsZero: true })}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100">
        {isSequenceMode ? (
          <>
            <button type="button" onClick={onMoveLeft} disabled={!canMoveLeft} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={onMoveRight} disabled={!canMoveRight} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            {onCopyToOtherDays && (
              <button
                type="button"
                onClick={onCopyToOtherDays}
                className="p-1.5 rounded-md text-gray-500 hover:text-orange-600 hover:bg-orange-50"
                title="Copy to other days"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}
            <button type="button" onClick={onEdit} className="p-1.5 rounded-md text-gray-500 hover:text-orange-600 hover:bg-orange-50" title="Edit">
              <Pencil className="w-4 h-4" />
            </button>
            <button type="button" onClick={onDelete} className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50" title="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ExerciseItemCard;
